import React, { useState, useRef, useCallback, CSSProperties } from 'react';
import { MeetingItemStatus } from '../models';
import { useTranslation } from 'react-i18next';
import { Tooltip, Popper, ClickAwayListener, Divider } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

/** Ordered list of all statuses for the dropdown */
const ALL_STATUSES: MeetingItemStatus[] = [
    MeetingItemStatus.NotStarted,
    MeetingItemStatus.InProgress,
    MeetingItemStatus.Completed,
    MeetingItemStatus.Cancelled,
    MeetingItemStatus.OnHold,
];

/** Statuses considered "inactive" — rendered below a separator */
const INACTIVE_STATUSES: MeetingItemStatus[] = [
    MeetingItemStatus.Cancelled,
    MeetingItemStatus.OnHold,
];

/** Inline style tokens per status */
const statusTokens: Record<MeetingItemStatus, {
    bg: string;
    hoverBg: string;
    text: string;
    border: string;
}> = {
    [MeetingItemStatus.Completed]: {
        bg: '#f0fdf4',
        hoverBg: '#dcfce7',
        text: '#16a34a',
        border: '#bbf7d0',
    },
    [MeetingItemStatus.Cancelled]: {
        bg: '#fef2f2',
        hoverBg: '#fee2e2',
        text: '#dc2626',
        border: '#fecaca',
    },
    [MeetingItemStatus.InProgress]: {
        bg: '#eff6ff',
        hoverBg: '#dbeafe',
        text: '#2563eb',
        border: '#bfdbfe',
    },
    [MeetingItemStatus.NotStarted]: {
        bg: '#f9fafb',
        hoverBg: '#f3f4f6',
        text: '#4b5563',
        border: '#e5e7eb',
    },
    [MeetingItemStatus.OnHold]: {
        bg: '#f9fafb',
        hoverBg: '#f3f4f6',
        text: '#6b7280',
        border: '#e5e7eb',
    },
};

/** Shared base style for the badge */
const badgeBase: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 130,
    whiteSpace: 'nowrap',
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'solid',
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: 5,
    paddingBottom: 5,
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: '20px',
    userSelect: 'none',
    fontFamily: 'inherit',
    cursor: 'default',
    outline: 'none',
    boxShadow: 'none',
    transition: 'background-color 0.15s',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
};

/** Dropdown container style */
const dropdownStyle: CSSProperties = {
    marginTop: 4,
    minWidth: 160,
    borderRadius: 10,
    padding: '6px 0',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)',
};

interface ItemStatusProps {
    status: MeetingItemStatus;
    lastModifiedBy?: string;
    /** When provided the badge becomes interactive with a dropdown */
    onChange?: (newStatus: MeetingItemStatus) => void;
    readOnly?: boolean;
}

const ItemStatus: React.FC<ItemStatusProps> = ({
    status,
    lastModifiedBy,
    onChange,
    readOnly = false,
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [hovered, setHovered] = useState(false);
    const anchorRef = useRef<HTMLButtonElement>(null);
    const tokens = statusTokens[status];
    const isInteractive = !readOnly && !!onChange;

    const label = t(`item_status.${status}`);
    const tooltipTitle = lastModifiedBy
        ? t('item_status.last_updated_by', { updater: lastModifiedBy })
        : '';

    const badgeStyle: CSSProperties = {
        ...badgeBase,
        backgroundColor: hovered && isInteractive ? tokens.hoverBg : tokens.bg,
        color: tokens.text,
        borderColor: tokens.border,
        cursor: isInteractive ? 'pointer' : 'default',
        gap: isInteractive ? 4 : 0,
    };

    const handleToggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setOpen((prev) => !prev);
    }, []);

    // Read-only badge (no dropdown arrow, no click)
    if (!isInteractive) {
        return (
            <Tooltip title={tooltipTitle}>
                <span style={badgeStyle}>
                    {label}
                </span>
            </Tooltip>
        );
    }

    // Interactive badge with dropdown
    return (
        <Tooltip title={!open ? tooltipTitle : ''}>
            <span style={{ position: 'relative', display: 'inline-block' }}>
                <button
                    ref={anchorRef}
                    type="button"
                    onClick={handleToggle}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    style={badgeStyle}
                >
                    {label}
                    <KeyboardArrowDownIcon sx={{ fontSize: 16, color: tokens.text }} />
                </button>

                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    placement="bottom-start"
                    style={{ zIndex: 1300 }}
                >
                    <ClickAwayListener onClickAway={() => setOpen(false)}>
                        <div style={dropdownStyle}>
                            {ALL_STATUSES.map((opt, idx) => {
                                const prevOpt = idx > 0 ? ALL_STATUSES[idx - 1] : null;
                                const showSeparator =
                                    prevOpt !== null &&
                                    !INACTIVE_STATUSES.includes(prevOpt) &&
                                    INACTIVE_STATUSES.includes(opt);
                                return (
                                    <React.Fragment key={opt}>
                                        {showSeparator && (
                                            <div style={{
                                                height: 1,
                                                backgroundColor: '#e5e7eb',
                                                margin: '6px 0',
                                            }} />
                                        )}
                                        <DropdownItem
                                            label={t(`item_status.${opt}`)}
                                            isSelected={opt === status}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onChange(opt);
                                                setOpen(false);
                                            }}
                                        />
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </ClickAwayListener>
                </Popper>
            </span>
        </Tooltip>
    );
};

/** Dropdown menu item with hover effect */
const DropdownItem: React.FC<{
    label: string;
    isSelected: boolean;
    onClick: (e: React.MouseEvent) => void;
}> = ({ label, isSelected, onClick }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <button
            type="button"
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '10px 16px',
                fontSize: '0.875rem',
                fontWeight: isSelected ? 600 : 400,
                color: '#111827',
                backgroundColor: hovered ? '#f3f4f6' : '#ffffff',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.15s',
                fontFamily: 'inherit',
                outline: 'none',
            }}
        >
            {label}
        </button>
    );
};

export default ItemStatus;